import { ErrorBoundary } from '@sentry/react';

import { PostFilter, type ExtendedProfile, type Profile } from '../../../../entities/post/models';
import { showDrillDownContextMenu } from '../../conversation/overlay/show-drill-down-context-menu';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useInsightFiltersFromSearchParams } from '../../filtered-posts/hook/use-insight-filters-from-search-params';
import { useFilteredProfiles } from '../hook/use-filtered-profiles';
import { FilteredProfilesHeader } from './filtered-profiles.header';

import { ProfileTable } from '@/features/(insight)/people/components/profile-table';
import { ErrorFallback } from '@/shared/components/error-fallback';
import { Scene, Stage } from '@/shared/components/stage/stage';
import { TableSkeleton } from '@/shared/components/table-skeleton/table-skeleton';
import { useFilteredPageDateRangeValue } from '@/shared/hooks/use-filtered-page-date-range';
import { useCurrentPageRoutingStatus } from '@/shared/hooks/use-is-routing';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useMemo } from 'react';
import { InsightFilterHeader } from '../../insight-layout/ui/insight-filter.header';
import { getProfileEngagementRate } from '../../shared/helpers/get-profile-engagement-rate';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

export function FilteredProfilesPage() {
  const fetchEnabled = useCurrentPageRoutingStatus({ currentPath: '/insight/filtered-profiles' });
  const insightFilters = useInsightFiltersFromSearchParams();
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const { startDate, endDate } = useFilteredPageDateRangeValue();
  const { query: selectedSavedQuery } = useSelectedSavedQuery();
  const queryId = selectedSavedQuery.id;
  const { publicFilter } = usePublicFilter(queryId);

  const queryName = selectedSavedQuery.name;

  const { data, isLoading } = useFilteredProfiles({
    queryId,
    startDate: startDate,
    endDate: endDate,
    filter: insightFilters,
    enabled: fetchEnabled,
    publicFilter,
  });

  const handleProfileClick = async (profile: Profile, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
      menus: ['view-posts'],
    });

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter: {
            [PostFilter.Profile]: profile.username,
          },
          dimension: PostFilter.Profile,
        });
        break;
      case 'close':
        break;
    }
  };

  const extendedProfiles: ExtendedProfile[] = useMemo(() => {
    return data?.profiles.map(getProfileEngagementRate) || [];
  }, [data?.profiles]);

  return (
    <div className="flex h-full flex-col">
      <InsightFilterHeader hideDateRange />
      <FilteredProfilesHeader
        queryName={queryName}
        profileCount={data?.totalCount}
        dateRange={{ startDate, endDate }}
      />

      <div className="flex-1 overflow-auto p-6">
        <Stage>
          <Scene status={isLoading}>
            <div className="p-6 py-0">
              <TableSkeleton
                columns={[
                  { className: 'w-[40px]' },
                  { className: 'w-[240px]' },
                  { className: 'flex-1 invisible' },
                  { className: 'w-[150px]' },
                ]}
                rows={10}
                rowHeight="h-[72px]"
              />
            </div>
          </Scene>
          <Scene default>
            <ErrorBoundary
              fallback={({ error, resetError }) => (
                <ErrorFallback
                  error={error as Error}
                  reset={resetError}
                  title="Failed to load profiles"
                />
              )}
            >
              <ProfileTable
                profiles={extendedProfiles || []}
                onProfileClick={handleProfileClick}
                isLoading={isLoading}
              />
            </ErrorBoundary>
          </Scene>
        </Stage>
      </div>
    </div>
  );
}
